import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AppSummaryRow } from './models/app-summary-models';
import { IoniconsOutline } from '@pt/pt-models';

@Component({
  selector: 'pt-widget-app-summary-row-rid-v0',
  template: `
    <div class="app-summary-preview-row">
      <div
        class="stat"
        *ngIf="!row.appAssessmentUnsupported && row.missingRIDValues"
      >
        <div class="legend">
          {{ warningLabel }}
        </div>
      </div>

      <div
        class="flex-row flex-column"
        *ngIf="!row.missingRIDValues; else onlyAlign"
      >
        <ng-container *ngIf="row.widgetValue?.length">
          <p class="new-value">
            {{ 'WIDGET_WARNING.APP_SUMMARY_DATA_NOT_ALIGNED' | translate }}
          </p>
          <div class="chart-container">
            <div class="charts" *ngFor="let chart of row.widgetValue">
              <div echarts class="chart" [options]="chart.options"></div>
              <div class="value">
                <!--                        {{ chart.value }}-->
              </div>
              <div class="detail">
                <div class="label">
                  {{ chart.label | async }}
                </div>
                <div class="description">
                  {{ chart.description | async }}
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex align-items-center flex-column w-100">
            <button
              class="btn btn-link text-link"
              [disabled]="!editable"
              (click)="alignValue.emit(row.appUUID)"
            >
              {{ 'BUTTON.UPDATE_WITH_VALUE' | translate }}
            </button>
            <pt-icon [key]="iconBackArrow" [size]="30"></pt-icon>
          </div>
        </ng-container>

        <div class="chart-container">
          <div
            class="chart-container-unsupported"
            *ngIf="row.appAssessmentUnsupported"
          >
            <div class="legend">
              {{ 'WIDGET_WARNING.APP_SUMMERY_NO_ASSESSMENT' | translate }}
            </div>
          </div>

          <ng-container *ngIf="!row.appAssessmentUnsupported">
            <div class="charts" *ngFor="let chart of row.actualQueryValue">
              <div echarts class="chart" [options]="chart.options"></div>
              <div class="value">
                <!--                          {{ chart.value }}-->
              </div>
              <div class="detail">
                <div class="label">
                  {{ chart.label | async }}
                </div>
                <div class="description">
                  {{ chart.description | async }}
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
      <ng-template #onlyAlign>
        <div
          class="d-flex align-items-center flex-column w-100"
          *ngIf="row.status !== 'EQUAL'"
        >
          <button
            class="btn btn-link text-link"
            [disabled]="!editable"
            (click)="alignValue.emit(row.appUUID)"
          >
            {{ 'BUTTON.UPDATE_WITH_VALUE' | translate }}
          </button>
        </div>
      </ng-template>
    </div>
  `,
  styleUrls: [`widget-app-summary-preview-v0.component.scss`],
})
export class WidgetAppSummaryRowRidV0Component implements OnChanges {
  @Input() row: AppSummaryRow;
  @Input() editable = false;
  @Input() warningLabel: string;

  @Output() alignValue = new EventEmitter<string>();
  iconBackArrow = IoniconsOutline.arrowDownOutline;

  state: AppSummaryRowRidState = AppSummaryRowRidState.NONE;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.row) {
      this.changeStatus();
    }
  }

  private changeStatus() {
    if (!this.row) {
      // Nessun dato disponibile: lo stato rimane NONE (default).
      // Il template non renderizza nulla di significativo.
      return;
    }

    switch (true) {
      /**
       * STATO S1 — Warning: dati RID non disponibili per l'applicativo.
       *
       * Condizioni:
       *  - `appAssessmentUnsupported` è false  → la valutazione è in linea
       *    di principio supportata per questo applicativo
       *  - `missingRIDValues` è true           → ma mancano i valori RID
       *
       * Cosa mostra il template:
       *  - Il `warningLabel` viene visualizzato nella sezione `.stat > .legend`
       *  - I chart (sia `diffCharts` che `charts`) NON vengono mostrati perché
       *    il blocco *ngIf="!row.missingRIDValues" è falso
       *  - Se lo status non è EQUAL compare il pulsante "UPDATE_WITH_VALUE"
       *    tramite il template #onlyAlign
       */
      case !this.row.appAssessmentUnsupported && this.row.missingRIDValues:
        this.state = AppSummaryRowRidState.S1;
        break;

      /**
       * STATO S2 — Dati RID presenti: visualizzazione completa.
       *
       * Condizioni:
       *  - `missingRIDValues` è false → i valori RID sono disponibili
       *
       * Cosa mostra il template (blocco *ngIf="!row.missingRIDValues"):
       *  - Se esistono `diffCharts`: mostra i grafici di differenza con
       *    il messaggio APP_SUMMARY_DATA_NOT_ALIGNED e il pulsante di allineamento
       *  - Se `appAssessmentUnsupported` è true: mostra il messaggio
       *    APP_SUMMERY_NO_ASSESSMENT al posto dei chart principali
       *  - Altrimenti: itera su `row.charts` e mostra i grafici principali
       *    con label e descrizione
       */
      case !this.row.missingRIDValues:
        this.state = AppSummaryRowRidState.S2;
        break;

      /**
       * STATO NONE (fallthrough implicito) — Caso non gestito / combinazione
       * inattesa di flag (es. appAssessmentUnsupported = true e
       * missingRIDValues = true contemporaneamente).
       *
       * Lo stato rimane NONE; il template renderizza comunque il blocco
       * `chart-container-unsupported` grazie al proprio *ngIf indipendente,
       * ma nessuna logica di stato viene applicata dal componente.
       */
      default:
        this.state = AppSummaryRowRidState.NONE;
        break;
    }
  }
}
export enum AppSummaryRowRidState {
  NONE = 'NONE',
  S1 = 'S1',
  S2 = 'S2'
}
